/* ============================================================
   Prefixbox SEARCH Autocomplete — THEME ASSETS ONLY (2 files)
   Pair with: assets/pfbx-autocomplete-custom.css
   Leave Prefixbox Autocomplete Custom CSS/JS empty.

   Prefixbox renders all suggestion rows. Before render, autocomplete
   API responses are augmented with any matching Shopify products
   (in-stock + out-of-stock) missing from Prefixbox suggestions,
   and internal/technical collections are stripped from Categories.
   Does NOT change search or collection page filters.
   ============================================================ */
(function () {
  if (window.__PFBX_AC_CUSTOM__) return;
  window.__PFBX_AC_CUSTOM__ = true;

  const AC_ROOT = '.prefixbox-autocomplete-container';
  const AC_INPUT = '#Search-Inline';
  const MIN_QUERY = 2;
  const MAX_MERGE = 3;

  /* Same denylist as theme Liquid (PLP / breadcrumbs / noindex) + technical scissors hosts */
  const HIDDEN_COLLECTION_HANDLES = new Set([
    'all',
    'fabg-eligible-products',
    'june-promo-eligible',
    'discountable-products',
    'suture-scissors-b-all-families',
    'suture-scissors-all-families',
  ]);

  /* Catch renamed / new promo-rule collections Prefixbox still indexes */
  const HIDDEN_COLLECTION_HANDLE_RE =
    /(^|-)(fabg[-_]?eligible|promo[-_]?eligible|discountable([-_]?products)?|eligible[-_]?products)($|-)/i;
  const HIDDEN_COLLECTION_TITLE_RE =
    /promo\s*eligible|fabg\s*eligible|discountable\s*products|excl\.?\s*sutures/i;

  let painting = false;
  let raf = null;
  let scheduleQueued = false;
  let inputDebounce = null;
  let oosAttemptQuery = '';
  const oosAttemptedSkus = new Set();
  const mergeCache = new Map();
  const productCache = new Map();
  const acOosUrls = new Set();
  const acOosSkus = new Set();
  const oosResolveCache = new Map();
  const pendingOosCards = new WeakSet();
  let lastOosQuery = '';
  let lastSuggestions = [];
  const OOS_LABEL = 'Out of Stock';

  const localePrefix = () => (window.Shopify?.routes?.root || '/').replace(/\/$/, '');

  const normKey = (value) => String(value || '').trim().toLowerCase();

  const normProductPath = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    try {
      return normKey(new URL(raw, location.origin).pathname.replace(/\/+$/, ''));
    } catch (e) {
      return normKey(raw.split('?')[0].split('#')[0]);
    }
  };

  const isShopifyOos = (variant, product) => {
    if (variant) {
      if (variant.available === false) return true;
      if (variant.inventory_management && Number(variant.inventory_quantity) <= 0) return true;
    }
    if (product?.available === false) return true;
    const variants = product?.variants || [];
    if (variants.length && variants.every((v) => v.available === false)) return true;
    return false;
  };

  const shopifyPriceToPrefixbox = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return n / 100;
  };

  const isSuggestionOos = (item) => {
    if (String(item?.type || '').toLowerCase() !== 'product') return false;
    if (item.stockAvailable === false || item.available === false || item.inStock === false) return true;
    if (item.isAvailable === false || item.outOfStock === true || item.soldOut === true) return true;
    if (Number(item.inventoryQuantity) === 0 || Number(item.inventory) === 0) return true;
    return false;
  };

  const beginOosQuery = (query) => {
    const qKey = normKey(query || '');
    if (!qKey || qKey === lastOosQuery) return;
    acOosUrls.clear();
    acOosSkus.clear();
    oosAttemptedSkus.clear();
    oosAttemptQuery = '';
    lastOosQuery = qKey;
  };

  const syncSuggestionAvailability = (suggestions) => {
    (suggestions || []).forEach((item) => {
      if (String(item?.type || '').toLowerCase() !== 'product') return;
      if (!isSuggestionOos(item)) return;
      item.stockAvailable = false;
      item.available = false;
      item.inStock = false;
      const url = normProductPath(item.url || item.link || '');
      if (url) acOosUrls.add(url);
      const sku = normKey(String(item.sku || item.SKU || '').replace(/^sku\s*:/i, ''));
      if (sku) acOosSkus.add(sku);
    });
  };

  const isSuggestionsRequest = (url) => {
    const s = String(url || '').toLowerCase();
    if (!s) return false;
    if (/#left-mode-filters|prefixbox-main|leftmode|collection-page|search-page/i.test(s)) return false;
    return /\/suggestions(\/|\?|$)/i.test(s) || /autocomplete\/suggestions/i.test(s);
  };

  const getQueryFromUrl = (url) => {
    try {
      return decodeURIComponent(new URL(url, location.origin).searchParams.get('pattern') || '').trim();
    } catch (e) {
      return '';
    }
  };

  const collectExisting = (suggestions) => {
    const urls = new Set();
    const skus = new Set();
    (suggestions || []).forEach((item) => {
      if (String(item?.type || '').toLowerCase() !== 'product') return;
      const url = normProductPath(item.url || item.link || '');
      if (url) urls.add(url);
      const sku = normKey(item.sku || item.SKU);
      if (sku) skus.add(sku);
    });
    return { urls, skus };
  };

  const findProductGroupId = (data) => {
    const groups = data?.groups || [];
    for (let i = 0; i < groups.length; i++) {
      const g = groups[i];
      const label = normKey(g?.name || g?.title || g?.label || '');
      if (label.includes('product')) return g.id ?? g.grp ?? g.groupId;
    }
    const productSuggestion = (data?.suggestions || []).find(
      (s) => String(s?.type || '').toLowerCase() === 'product' && (s.grp != null || s.groupId != null)
    );
    return productSuggestion?.grp ?? productSuggestion?.groupId ?? null;
  };

  const fetchSuggestProducts = async (query) => {
    const cacheKey = normKey(query);
    if (mergeCache.has(cacheKey)) return mergeCache.get(cacheKey);

    const base = localePrefix();
    const url =
      base +
      '/search/suggest.json?q=' +
      encodeURIComponent(query) +
      '&resources[type]=product&resources[limit]=12&resources[options][fields]=title,variants.sku,vendor,product_type,body';

    const task = fetch(url)
      .then((res) => res.json())
      .then((json) => json.resources?.results?.products || [])
      .catch(() => []);

    mergeCache.set(cacheKey, task);
    setTimeout(() => mergeCache.delete(cacheKey), 30000);
    return task;
  };

  const fetchProduct = async (handle) => {
    if (!handle) return null;
    const cacheKey = normKey(handle);
    if (productCache.has(cacheKey)) return productCache.get(cacheKey);

    const task = fetch(localePrefix() + '/products/' + handle + '.js')
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null);

    productCache.set(cacheKey, task);
    setTimeout(() => productCache.delete(cacheKey), 60000);
    return task;
  };

  const indexOosFromSuggestQuery = async (query) => {
    const q = String(query || '').trim();
    if (q.length < MIN_QUERY) return;

    const cacheKey = 'index|' + normKey(q);
    if (oosResolveCache.has(cacheKey)) return oosResolveCache.get(cacheKey);

    const task = (async () => {
      const items = await fetchSuggestProducts(q);
      await Promise.all(
        items.map(async (item) => {
          const handle = (item.url || '').split('/products/')[1]?.split('?')[0];
          if (!handle) return;
          const product = await fetchProduct(handle);
          if (!product?.variants?.length) return;
          product.variants.forEach((variant) => {
            if (isShopifyOos(variant, product)) rememberOosProduct(product, variant);
          });
        })
      );
    })();

    oosResolveCache.set(cacheKey, task);
    setTimeout(() => oosResolveCache.delete(cacheKey), 30000);
    return task;
  };

  const warmOosForQuery = (query) => {
    const q = String(query || '').trim();
    if (q.length < MIN_QUERY) return;
    if (inputDebounce) clearTimeout(inputDebounce);
    inputDebounce = setTimeout(() => {
      inputDebounce = null;
      beginOosQuery(q);
      indexOosFromSuggestQuery(q)
        .then(() => schedule())
        .catch(() => {});
    }, 180);
  };

  const getHandleFromSuggestion = (item) => {
    if (item?.handle) return String(item.handle).split('?')[0];
    if (item?.productHandle) return String(item.productHandle).split('?')[0];
    const paths = [item?.url, item?.link, item?.productUrl, item?.href, item?.uri].filter(Boolean);
    for (let i = 0; i < paths.length; i++) {
      const handle = normProductPath(paths[i]).split('/products/')[1]?.split('/')[0];
      if (handle) return handle;
    }
    return '';
  };

  const markSuggestionOos = (item) => {
    item.stockAvailable = false;
    item.available = false;
    item.inStock = false;
    item.outOfStock = true;
  };

  const findProductBySku = async (sku, queryHint) => {
    const skuKey = normKey(String(sku || '').replace(/^sku\s*:/i, ''));
    if (!skuKey) return null;

    const cacheKey = skuKey + '|' + normKey(queryHint || '');
    if (oosResolveCache.has(cacheKey)) return oosResolveCache.get(cacheKey);

    const task = (async () => {
      const queries = [];
      if (queryHint) queries.push(String(queryHint).trim());
      if (!queries.includes(skuKey)) queries.push(skuKey);

      for (let q = 0; q < queries.length; q++) {
        const items = await fetchSuggestProducts(queries[q]);
        const matches = await Promise.all(
          items.map(async (item) => {
            const handle = (item.url || '').split('/products/')[1]?.split('?')[0];
            if (!handle) return null;
            const product = await fetchProduct(handle);
            if (!product?.variants?.length) return null;
            const variant = product.variants.find((v) => normKey(v.sku) === skuKey);
            return variant ? { product, variant } : null;
          })
        );
        const hit = matches.find(Boolean);
        if (hit) return hit;
      }
      return null;
    })();

    oosResolveCache.set(cacheKey, task);
    setTimeout(() => oosResolveCache.delete(cacheKey), 30000);
    return task;
  };

  const rememberOosProduct = (product, variant) => {
    const url = normProductPath(product?.url || '/products/' + (product?.handle || ''));
    if (url) acOosUrls.add(url);
    const sku = normKey(String(variant?.sku || '').replace(/^sku\s*:/i, ''));
    if (sku) acOosSkus.add(sku);
  };

  const enrichAvailabilityFromShopify = async (suggestions, queryHint) => {
    const products = (suggestions || []).filter((s) => String(s?.type || '').toLowerCase() === 'product');
    await Promise.all(
      products.map(async (item) => {
        const skuKey = normKey(String(item.sku || item.SKU || '').replace(/^sku\s*:/i, ''));
        let product = null;
        let variant = null;

        const handle = getHandleFromSuggestion(item);
        if (handle) {
          product = await fetchProduct(handle);
          if (product) {
            variant =
              (skuKey && product.variants?.find((v) => normKey(v.sku) === skuKey)) || product.variants?.[0];
          }
        }

        if (!product && skuKey) {
          const match = await findProductBySku(skuKey, queryHint);
          if (match) {
            product = match.product;
            variant = match.variant;
          }
        }

        if (!product || !isShopifyOos(variant, product)) return;
        markSuggestionOos(item);
        rememberOosProduct(product, variant);
      })
    );
  };

  const pickVariant = (product, query) => {
    const q = normKey(query);
    const variants = product?.variants || [];
    return (
      variants.find((v) => normKey(v.sku) === q) ||
      variants.find((v) => normKey(v.sku).includes(q)) ||
      variants.find((v) => v.sku) ||
      variants[0] ||
      null
    );
  };

  const scoreMatch = (product, variant, query) => {
    const q = normKey(query);
    if (!q) return 0;
    const sku = normKey(variant?.sku);
    const title = normKey(product?.title);
    if (sku === q) return 10000;
    if (sku && sku.startsWith(q)) return 8500;
    if (sku && q.startsWith(sku)) return 8400;
    if (sku && sku.includes(q)) return 8000;
    if (sku && q.includes(sku)) return 7500;
    if (title.includes(q)) return 5000;
    return 0;
  };

  const scoreSuggestionItem = (item, query) => {
    if (String(item?.type || '').toLowerCase() !== 'product') return -1;
    const q = normKey(query);
    if (!q) return 0;
    const sku = normKey(String(item.sku || item.SKU || '').replace(/^sku\s*:/i, ''));
    const title = normKey(item.displayText || item.text || item.title || '');
    if (sku === q) return 10000;
    if (sku && sku.startsWith(q)) return 8500;
    if (sku && q.startsWith(sku)) return 8400;
    if (sku && sku.includes(q)) return 8000;
    if (sku && q.includes(sku)) return 7500;
    if (title.includes(q)) return 5000;
    return 0;
  };

  const sortSuggestionsByRelevance = (suggestions, query) => {
    return (suggestions || [])
      .map((item, index) => ({ item, index }))
      .sort((a, b) => {
        const scoreDiff = scoreSuggestionItem(b.item, query) - scoreSuggestionItem(a.item, query);
        if (scoreDiff) return scoreDiff;
        return a.index - b.index;
      })
      .map((entry) => entry.item);
  };

  const getCollectionHandleFromSuggestion = (item) => {
    const direct = normKey(item?.handle || item?.collectionHandle || item?.categoryHandle || '');
    if (direct) return direct.split('/').pop();
    const paths = [item?.url, item?.link, item?.href, item?.uri, item?.categoryUrl].filter(Boolean);
    for (let i = 0; i < paths.length; i++) {
      try {
        const pathname = new URL(String(paths[i]), location.origin).pathname;
        const match = pathname.match(/\/collections\/([^/?#]+)/i);
        if (match) return normKey(decodeURIComponent(match[1]));
      } catch (e) {
        const match = String(paths[i]).match(/\/collections\/([^/?#]+)/i);
        if (match) return normKey(decodeURIComponent(match[1]));
      }
    }
    return '';
  };

  const getSuggestionLabel = (item) =>
    String(item?.displayText || item?.text || item?.title || item?.name || item?.label || '').trim();

  const isCategorySuggestion = (item) => {
    const type = String(item?.type || item?.dataSetType || '').toLowerCase();
    if (type.includes('categor') || type.includes('collection')) return true;
    const handle = getCollectionHandleFromSuggestion(item);
    if (handle) return true;
    return false;
  };

  const isHiddenInternalCollection = (item) => {
    const handle = getCollectionHandleFromSuggestion(item);
    if (handle && HIDDEN_COLLECTION_HANDLES.has(handle)) return true;
    if (handle && HIDDEN_COLLECTION_HANDLE_RE.test(handle)) return true;
    const label = getSuggestionLabel(item);
    if (label && HIDDEN_COLLECTION_TITLE_RE.test(label)) return true;
    return false;
  };

  const filterInternalCollections = (suggestions) =>
    (suggestions || []).filter((item) => {
      if (!isCategorySuggestion(item)) return true;
      return !isHiddenInternalCollection(item);
    });

  const finalizeSuggestions = async (suggestions, query) => {
    const cleaned = filterInternalCollections(suggestions);
    await enrichAvailabilityFromShopify(cleaned, query);
    syncSuggestionAvailability(cleaned);
    return sortSuggestionsByRelevance(cleaned, query);
  };

  const toSuggestion = (product, variant, grp, query) => {
    const handle = product.handle;
    const href = localePrefix() + '/products/' + handle;
    const image =
      product.featured_image ||
      product.images?.[0] ||
      variant?.featured_image?.src ||
      '';
    const imageUrl = typeof image === 'string' ? image : image?.src || image?.url || '';
    const oos = isShopifyOos(variant, product);
    const priceCents = variant?.price != null ? Number(variant.price) : Number(product.price) || 0;
    const compareCents =
      variant?.compare_at_price != null ? Number(variant.compare_at_price) : Number(product.compare_at_price) || 0;

    return {
      type: 'product',
      dataSetType: 'elasticSearchProduct',
      displayText: product.title,
      text: product.title,
      url: href,
      link: href,
      imageUrl: imageUrl,
      sku: variant?.sku || '',
      priceValue: shopifyPriceToPrefixbox(priceCents),
      oldPriceValue: compareCents ? shopifyPriceToPrefixbox(compareCents) : 0,
      stockAvailable: !oos,
      available: !oos,
      inStock: !oos,
      outOfStock: oos,
      grp: grp,
      groupId: grp,
    };
  };

  const mergeMissingProducts = async (data, query) => {
    const q = String(query || '').trim();
    if (!data || q.length < MIN_QUERY) return data;

    const suggestions = Array.isArray(data.suggestions) ? data.suggestions.slice() : [];
    beginOosQuery(q);
    const items = await fetchSuggestProducts(q);

    if (!items.length) {
      data.suggestions = await finalizeSuggestions(suggestions, q);
      return data;
    }

    const { urls, skus } = collectExisting(suggestions);
    const grp = findProductGroupId(data);
    const picks = [];

    for (const item of items) {
      const handle = (item.url || '').split('/products/')[1]?.split('?')[0];
      const urlKey = normProductPath(item.url || '');
      if (urlKey && urls.has(urlKey)) continue;

      const product = (await fetchProduct(handle)) || item;
      const variant = pickVariant(product, q);
      const score = scoreMatch(product, variant, q);
      if (!score) continue;

      const skuKey = normKey(variant?.sku);
      if (skuKey && skus.has(skuKey)) continue;

      picks.push({ score, suggestion: toSuggestion(product, variant, grp, q) });
    }

    if (!picks.length) {
      data.suggestions = await finalizeSuggestions(suggestions, q);
      return data;
    }

    picks.sort((a, b) => b.score - a.score);
    const merged = picks.slice(0, MAX_MERGE).map((p) => p.suggestion);
    data.suggestions = await finalizeSuggestions(merged.concat(suggestions), q);
    return data;
  };

  const processSuggestionsResponse = async (url, rawText) => {
    const query = getQueryFromUrl(url);
    if (query.length < MIN_QUERY) return rawText;
    const data = JSON.parse(rawText);
    const next = await mergeMissingProducts(data, query);
    lastSuggestions = Array.isArray(next.suggestions) ? next.suggestions : [];
    return JSON.stringify(next);
  };

  const patchAutocompleteFetch = () => {
    if (window.__PFBX_AC_FETCH_PATCHED__) return;
    window.__PFBX_AC_FETCH_PATCHED__ = true;

    const nativeFetch = window.fetch.bind(window);
    window.fetch = async function (input, init) {
      const response = await nativeFetch(input, init);
      try {
        const url = typeof input === 'string' ? input : input?.url;
        if (!isSuggestionsRequest(url)) return response;

        const query = getQueryFromUrl(url);
        if (query.length < MIN_QUERY) return response;

        const ct = response.headers.get('content-type') || '';
        if (!ct.includes('json')) return response;

        const rawText = await response.clone().text();
        const nextText = await processSuggestionsResponse(url, rawText);
        return new Response(nextText, {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
        });
      } catch (e) {
        return response;
      }
    };
  };

  patchAutocompleteFetch();

  const setText = (el, text) => {
    if (!el || !text || el.textContent === text) return;
    el.textContent = text;
  };

  const unwrapLegacyLayout = (root) => {
    const scrollBody = root.querySelector(':scope > .pfbx-ac-scroll-body');
    if (!scrollBody) return;
    while (scrollBody.firstChild) {
      root.insertBefore(scrollBody.firstChild, scrollBody);
    }
    scrollBody.remove();
  };

  const PRICE_SEL =
    '.prefixbox-autocomplete-price, .prefixbox-search-price-container, .prefixbox-price-container, .prefixbox-old-price-container, [class*="autocomplete-price"]:not([class*="sku"]), [class*="old-price"]';

  const stripUsd = (root) => {
    if (!root) return;
    root.querySelectorAll(PRICE_SEL).forEach((el) => {
      if (/sku/i.test(el.className || '')) return;
      const t = (el.textContent || '').replace(/\s*USD\b/gi, '').replace(/\s+/g, ' ').trim();
      setText(el, t);
    });
  };

  const getProductCardUrl = (card) => {
    const href =
      card.getAttribute('href') ||
      card.querySelector('a[href*="/products/"]')?.getAttribute('href') ||
      card.closest('a[href*="/products/"]')?.getAttribute('href') ||
      '';
    return normProductPath(href);
  };

  const getProductCardSku = (card) => {
    const skuEl = card.querySelector(
      '.pfbx-ac-product-sku, .prefixbox-autocomplete-sku-container, .prefixbox-autocomplete-sku, [class*="sku-container"]'
    );
    return normKey(String(skuEl?.textContent || '').replace(/^sku\s*:/i, ''));
  };

  const cardIsOos = (card) => {
    const url = getProductCardUrl(card);
    if (url && acOosUrls.has(url)) return true;
    const sku = getProductCardSku(card);
    if (sku && acOosSkus.has(sku)) return true;

    if (sku && lastSuggestions.length) {
      const hit = lastSuggestions.find((item) => {
        if (String(item?.type || '').toLowerCase() !== 'product') return false;
        const itemSku = normKey(String(item.sku || item.SKU || '').replace(/^sku\s*:/i, ''));
        return itemSku && itemSku === sku;
      });
      if (hit && isSuggestionOos(hit)) {
        rememberOosProduct(
          { url: hit.url || hit.link, handle: getHandleFromSuggestion(hit) },
          { sku: hit.sku || hit.SKU }
        );
        return true;
      }
    }

    return false;
  };

  const hideEmptyStockSlots = (card) => {
    const right = card.querySelector('.prefixbox-autocomplete-right, .pfbx-ac-product-price-wrap');
    if (!right) return;
    right.querySelectorAll(':scope > *').forEach((el) => {
      if (
        el.matches(
          '.pfbx-ac-product-price, .prefixbox-autocomplete-price-container, .prefixbox-autocomplete-price, .prefixbox-search-price-container, .prefixbox-price-container, .prefixbox-old-price-container, .pfbx-ac-compare-price, [class*="old-price"]'
        )
      ) {
        return;
      }
      const text = String(el.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text) el.classList.add('pfbx-ac-hide-stock');
    });
  };

  const getSearchQuery = () => document.querySelector(AC_INPUT)?.value?.trim() || '';

  const resolveProductBySku = async (sku, queryHint) => {
    const skuKey = normKey(String(sku || '').replace(/^sku\s*:/i, ''));
    if (!skuKey) return null;

    if (lastSuggestions.length) {
      for (let i = 0; i < lastSuggestions.length; i++) {
        const item = lastSuggestions[i];
        if (String(item?.type || '').toLowerCase() !== 'product') continue;
        const itemSku = normKey(String(item.sku || item.SKU || '').replace(/^sku\s*:/i, ''));
        if (itemSku !== skuKey) continue;
        const handle = getHandleFromSuggestion(item);
        if (!handle) continue;
        const product = await fetchProduct(handle);
        if (!product?.variants?.length) continue;
        const variant =
          product.variants.find((v) => normKey(v.sku) === skuKey) || product.variants[0];
        return { product, variant };
      }
    }

    return findProductBySku(sku, queryHint);
  };

  const ensureSkuMetaRow = (card) => {
    if (card.dataset.pfbxAcSkuMeta === '1') {
      return card.querySelector(':scope > .pfbx-ac-sku-meta, .pfbx-ac-sku-meta');
    }

    let meta = card.querySelector(':scope > .pfbx-ac-sku-meta, .pfbx-ac-sku-meta');
    if (meta) {
      card.dataset.pfbxAcSkuMeta = '1';
      return meta;
    }

    const skuEl = card.querySelector(
      '.pfbx-ac-product-sku, .prefixbox-autocomplete-sku-container, .prefixbox-autocomplete-sku, [class*="sku-container"]'
    );
    if (!skuEl || skuEl.closest('.pfbx-ac-sku-meta')) return null;

    meta = document.createElement('div');
    meta.className = 'pfbx-ac-sku-meta';
    skuEl.parentNode.insertBefore(meta, skuEl);
    meta.appendChild(skuEl);
    card.dataset.pfbxAcSkuMeta = '1';
    return meta;
  };

  const finalizeOosLabelPlacement = (card) => {
    const meta = ensureSkuMetaRow(card);
    if (!meta) return;

    card.querySelectorAll('.pfbx-ac-stock-label').forEach((label) => {
      if (!meta.contains(label)) meta.appendChild(label);
    });
  };

  const resolveCardOos = async (card) => {
    if (cardIsOos(card)) return true;

    const sku = getProductCardSku(card);
    if (!sku) return false;

    const match = await resolveProductBySku(sku, getSearchQuery());
    if (!match || !isShopifyOos(match.variant, match.product)) return false;

    rememberOosProduct(match.product, match.variant);
    return true;
  };

  const injectOosLabel = (card) => {
    if (card.querySelector('.pfbx-ac-sku-meta .pfbx-ac-stock-label')) {
      finalizeOosLabelPlacement(card);
      return;
    }

    hideEmptyStockSlots(card);
    card.classList.add('pfbx-ac-has-oos');

    const meta = ensureSkuMetaRow(card);
    const host = meta || card;
    const label = document.createElement('span');
    label.className = 'pfbx-ac-stock-label pfbx-product-stock out-of-stock';
    label.textContent = OOS_LABEL;
    host.appendChild(label);
    finalizeOosLabelPlacement(card);
  };

  const resolveVisibleCardsOos = (root) => {
    const qKey = normKey(getSearchQuery());
    if (qKey !== oosAttemptQuery) {
      oosAttemptQuery = qKey;
      oosAttemptedSkus.clear();
    }

    const cards = root.querySelectorAll(
      '.prefixbox-autocomplete-product:not(.prefixbox-autocomplete-product-container)'
    );
    cards.forEach((card) => {
      if (card.querySelector('.pfbx-ac-sku-meta .pfbx-ac-stock-label')) return;

      if (cardIsOos(card)) {
        injectOosLabel(card);
        return;
      }

      const sku = getProductCardSku(card);
      if (!sku || pendingOosCards.has(card) || oosAttemptedSkus.has(sku)) return;

      oosAttemptedSkus.add(sku);
      pendingOosCards.add(card);
      resolveCardOos(card)
        .then((oos) => {
          pendingOosCards.delete(card);
          if (!oos || !card.isConnected) return;
          injectOosLabel(card);
        })
        .catch(() => {
          pendingOosCards.delete(card);
        });
    });
  };

  const ensureOosLabel = (card) => {
    card
      .querySelectorAll(
        '.pfbx-product-out-of-stock, [class*="out-of-stock"], [class*="out_of_stock"], [class*="outofstock"]'
      )
      .forEach((el) => {
        el.classList.add('pfbx-ac-stock-label');
        card.classList.add('pfbx-ac-has-oos');
      });

    if (!card.querySelector('.pfbx-ac-stock-label')) {
      card.querySelectorAll('.prefixbox-autocomplete-body *, .pfbx-ac-product-content *').forEach((el) => {
        if (el.children.length) return;
        if (String(el.textContent || '').trim().toLowerCase() === OOS_LABEL.toLowerCase()) {
          el.classList.add('pfbx-ac-stock-label');
          card.classList.add('pfbx-ac-has-oos');
        }
      });
    }

    finalizeOosLabelPlacement(card);

    if (card.querySelector('.pfbx-ac-sku-meta .pfbx-ac-stock-label')) {
      finalizeOosLabelPlacement(card);
      return;
    }

    if (cardIsOos(card)) {
      injectOosLabel(card);
      return;
    }

    ensureSkuMetaRow(card);
  };

  const reorderProductRows = (root, query) => {
    const q = String(query || '').trim();
    if (q.length < MIN_QUERY) return;

    const containers = Array.from(
      root.querySelectorAll(
        '.pfbx-ac-products .prefixbox-autocomplete-product-container, .prefixbox-group-container.group-id-3 .prefixbox-autocomplete-product-container, [class*="group-id-3"] .prefixbox-autocomplete-product-container'
      )
    );
    if (containers.length < 2) return;

    const parent = containers[0].parentElement;
    if (!parent) return;

    const scored = containers.map((container, index) => {
      const card =
        container.querySelector('.prefixbox-autocomplete-product:not(.prefixbox-autocomplete-product-container)') ||
        container.querySelector('.prefixbox-autocomplete-product') ||
        container;
      const sku = getProductCardSku(card);
      const title = card.querySelector(
        '.pfbx-ac-product-title, .prefixbox-autocomplete-title-container, [class*="product-title"]'
      );
      const item = {
        type: 'product',
        sku: sku,
        title: title?.textContent || '',
      };
      return { container, score: scoreSuggestionItem(item, q), index };
    });

    scored.sort((a, b) => b.score - a.score || a.index - b.index);
    if (scored.every((entry, idx) => entry.index === idx)) return;

    scored.forEach(({ container }) => parent.appendChild(container));
  };

  const paintProduct = (card) => {
    card.classList.add('pfbx-ac-product');

    const imgWrap =
      card.querySelector('.prefixbox-autocomplete-left') || card.querySelector('img')?.parentElement;
    if (imgWrap) imgWrap.classList.add('pfbx-ac-product-image');

    const body = card.querySelector('.prefixbox-autocomplete-body');
    if (body) body.classList.add('pfbx-ac-product-content');

    const title = card.querySelector(
      '.prefixbox-autocomplete-title-container, .prefixbox-autocomplete-product-title, [class*="product-title"]'
    );
    if (title) title.classList.add('pfbx-ac-product-title');

    const right = card.querySelector('.prefixbox-autocomplete-right');
    if (right) right.classList.add('pfbx-ac-product-price-wrap');

    card
      .querySelectorAll(
        '.prefixbox-old-price-container, [class*="old-price-container"], [class*="old-price"]:not([class*="sku"])'
      )
      .forEach((el) => {
        if (/sku/i.test(el.className || '')) return;
        el.classList.add('pfbx-ac-compare-price');
      });

    card.classList.remove('pfbx-ac-on-sale');
    card.querySelectorAll('.pfbx-ac-sale-price').forEach((el) => el.classList.remove('pfbx-ac-sale-price'));

    const priceEls = [];
    card
      .querySelectorAll(
        '.prefixbox-autocomplete-price, .prefixbox-search-price-container, .prefixbox-price-container, [class*="autocomplete-price"]:not([class*="sku"])'
      )
      .forEach((el) => {
        if (/sku|old-price/i.test(el.className || '')) return;
        if (el.closest('[class*="old-price"]')) return;
        el.classList.add('pfbx-ac-product-price');
        priceEls.push(el);
      });

    const hasCompare = card.querySelector(
      '.pfbx-ac-compare-price, .prefixbox-old-price-container, [class*="old-price-container"]'
    );
    if (hasCompare) {
      card.classList.add('pfbx-ac-on-sale');
      priceEls.forEach((el) => el.classList.add('pfbx-ac-sale-price'));
    } else if (right) {
      right.classList.add('pfbx-ac-price-flat');
    }

    card
      .querySelectorAll(
        '.prefixbox-autocomplete-sku-container, .prefixbox-autocomplete-sku, [class*="sku-container"]'
      )
      .forEach((el) => {
        if (/price|old-price|title|product/i.test(el.className || '')) return;
        el.classList.add('pfbx-ac-product-sku');
      });

    card
      .querySelectorAll('[class*="vendor"],[class*="brand"],.prefixbox-autocomplete-vendor')
      .forEach((el) => el.classList.add('pfbx-ac-hide-brand'));

    hideEmptyStockSlots(card);
    ensureSkuMetaRow(card);
    ensureOosLabel(card);
  };

  const observeAc = (root) => {
    if (root.dataset.pfbxAcObserved) return;
    root.dataset.pfbxAcObserved = '1';
    new MutationObserver((mutations) => {
      if (root.dataset.pfbxAcPainting === '1') return;
      let significant = false;
      for (let i = 0; i < mutations.length; i++) {
        const m = mutations[i];
        if (m.type !== 'childList' || !m.addedNodes.length) continue;
        for (let j = 0; j < m.addedNodes.length; j++) {
          const n = m.addedNodes[j];
          if (n.nodeType !== 1) continue;
          if (
            n.matches?.(
              '.prefixbox-autocomplete-product-container, .prefixbox-autocomplete-product, .prefixbox-group-container, .prefixbox-autocomplete-submit-button'
            ) ||
            n.querySelector?.(
              '.prefixbox-autocomplete-product-container, .prefixbox-autocomplete-product, .prefixbox-group-container'
            )
          ) {
            significant = true;
            break;
          }
        }
        if (significant) break;
      }
      if (significant) schedule();
    }).observe(root, { childList: true, subtree: true });
  };

  const paintRoot = (root) => {
    if (!root || root.nodeType !== 1) return;

    root.dataset.pfbxAcPainting = '1';
    try {
    unwrapLegacyLayout(root);
    root.classList.add('pfbx-ac-ready');
    observeAc(root);
    stripUsd(root);

    const groupCount = root.querySelectorAll('.prefixbox-group-container, [class*="group-container"]').length;
    if (root.dataset.pfbxAcGroupCount !== String(groupCount)) {
      delete root.dataset.pfbxAcSections;
      root.dataset.pfbxAcGroupCount = String(groupCount);
      root.querySelectorAll('[data-pfbx-ac-product]').forEach((el) => {
        delete el.dataset.pfbxAcProduct;
        delete el.dataset.pfbxAcSkuMeta;
      });
    }

    if (!root.dataset.pfbxAcSections) {
      root.querySelectorAll('.prefixbox-group-container, [class*="group-container"]').forEach((g) => {
        const cls = String(g.className || '').toLowerCase();
        if (cls.includes('group-id-3')) {
          g.classList.add('pfbx-ac-section', 'pfbx-ac-section-products', 'pfbx-ac-products');
        } else if (cls.includes('group-id-2') || cls.includes('categor')) {
          g.classList.add('pfbx-ac-section', 'pfbx-ac-section-categories', 'pfbx-ac-categories');
        } else if (cls.includes('group-id-1') || cls.includes('keyword') || cls.includes('suggestion')) {
          g.classList.add('pfbx-ac-section', 'pfbx-ac-section-suggestions', 'pfbx-ac-keywords');
        }
      });

      root.querySelectorAll('.col-2, [class*="col-2"]').forEach((el) => {
        el.classList.add('pfbx-ac-products-col');
      });
      root.querySelectorAll('.col-1, [class*="col-1"]').forEach((el) => {
        el.classList.add('pfbx-ac-left-stack');
      });

      root.dataset.pfbxAcSections = '1';
    }

    root
      .querySelectorAll('.prefixbox-autocomplete-product:not(.prefixbox-autocomplete-product-container)')
      .forEach((card) => {
        paintProduct(card);
        card.dataset.pfbxAcProduct = '1';
      });

    resolveVisibleCardsOos(root);
    reorderProductRows(root, getSearchQuery());

    stripUsd(root);

    /* DOM backup: hide internal/technical category rows Prefixbox may still paint */
    root
      .querySelectorAll(
        '.prefixbox-autocomplete-category, .pfbx-ac-section-categories a, .prefixbox-group-container.group-id-2 a, .pfbx-ac-categories a'
      )
      .forEach((el) => {
        const href = el.getAttribute('href') || el.querySelector?.('a')?.getAttribute('href') || '';
        const label = (el.textContent || '').replace(/\s+/g, ' ').trim();
        const handleMatch = String(href).match(/\/collections\/([^/?#]+)/i);
        const handle = handleMatch ? normKey(decodeURIComponent(handleMatch[1])) : '';
        const hide =
          (handle && HIDDEN_COLLECTION_HANDLES.has(handle)) ||
          (handle && HIDDEN_COLLECTION_HANDLE_RE.test(handle)) ||
          HIDDEN_COLLECTION_TITLE_RE.test(label);
        if (!hide) return;
        const row =
          el.closest('.prefixbox-autocomplete-category, li, [class*="suggestion"], [class*="category"]') || el;
        row.style.setProperty('display', 'none', 'important');
        row.setAttribute('hidden', '');
        row.setAttribute('data-pfbx-ac-hidden-internal', '1');
      });

    root.querySelectorAll('.pfbx-ac-section-categories, .prefixbox-group-container.group-id-2').forEach((section) => {
      const rows = section.querySelectorAll(
        '.prefixbox-autocomplete-category, a[href*="/collections/"]'
      );
      if (!rows.length) return;
      const visible = [...rows].some((row) => {
        if (row.getAttribute('data-pfbx-ac-hidden-internal') === '1') return false;
        if (row.hasAttribute('hidden')) return false;
        const style = window.getComputedStyle(row);
        return style.display !== 'none' && style.visibility !== 'hidden';
      });
      if (!visible) {
        section.style.setProperty('display', 'none', 'important');
        section.setAttribute('data-pfbx-ac-empty-categories', '1');
      } else {
        section.style.removeProperty('display');
        section.removeAttribute('data-pfbx-ac-empty-categories');
      }
    });

    root.querySelectorAll('[class*="group-id-1"] [class*="title"], .prefixbox-group-1-title').forEach((el) => {
      const t = (el.textContent || '').trim();
      if (/query suggestions/i.test(t)) setText(el, 'Suggestions');
    });

    root.querySelectorAll('.prefixbox-autocomplete-submit-button').forEach((btn, idx) => {
      if (idx === 0) {
        btn.classList.add('pfbx-ac-view-all');
        if (!/view all results/i.test((btn.textContent || '').trim())) {
          setText(btn, 'View all results →');
        }
      }
      btn.removeAttribute('hidden');
      btn.style.removeProperty('display');
      btn.style.removeProperty('visibility');
    });
    } finally {
      delete root.dataset.pfbxAcPainting;
    }
  };

  const polish = () => {
    if (painting) return;
    painting = true;
    try {
      document.querySelectorAll(AC_ROOT).forEach((root) => {
        stripUsd(root);
        paintRoot(root);
      });
    } catch (e) {
      /* never break the page */
    } finally {
      painting = false;
    }
  };

  const schedule = () => {
    if (scheduleQueued) return;
    scheduleQueued = true;
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      scheduleQueued = false;
      raf = null;
      polish();
    });
  };

  const start = () => {
    polish();

    const watch = document.querySelector('.self-service') || document.querySelector('.header__search-inline');
    if (watch) {
      new MutationObserver((mutations) => {
        for (let i = 0; i < mutations.length; i++) {
          const nodes = mutations[i].addedNodes;
          for (let j = 0; j < nodes.length; j++) {
            const n = nodes[j];
            if (n.nodeType !== 1) continue;
            if (n.matches?.(AC_ROOT) || n.querySelector?.(AC_ROOT)) {
              schedule();
              return;
            }
          }
        }
      }).observe(watch, { childList: true, subtree: true });
    }

    document.addEventListener(
      'focusin',
      (e) => {
        if (!e.target?.matches?.(AC_INPUT)) return;
        warmOosForQuery(e.target.value);
      },
      true
    );

    document.addEventListener(
      'input',
      (e) => {
        if (!e.target?.matches?.(AC_INPUT)) return;
        warmOosForQuery(e.target.value);
        schedule();
      },
      true
    );
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
